import { COLORS, PITCH_MARKINGS } from './constants.js';

export class Renderer {
    constructor(ctx, canvas) {
        this.ctx = ctx;
        this.canvas = canvas;
        this.grassPattern = null;
        this.initGrassPattern();
    }
    
    initGrassPattern() {
        // Create grass texture pattern
        const patternCanvas = document.createElement('canvas');
        patternCanvas.width = 40;
        patternCanvas.height = 40;
        const patternCtx = patternCanvas.getContext('2d');
        
        // Base grass color
        patternCtx.fillStyle = COLORS.PITCH_GREEN;
        patternCtx.fillRect(0, 0, 40, 40);
        
        // Use deterministic pseudo-random values for consistent grass pattern
        const seed = 12345; // Fixed seed for consistent pattern
        function seededRandom(seed) {
            const x = Math.sin(seed) * 10000;
            return x - Math.floor(x);
        }
        
        let currentSeed = seed;
        
        // Add grass texture
        patternCtx.fillStyle = COLORS.PITCH_DARK;
        for (let i = 0; i < 20; i++) {
            currentSeed++;
            const x = seededRandom(currentSeed) * 40;
            currentSeed++;
            const y = seededRandom(currentSeed) * 40;
            currentSeed++;
            const width = 1 + seededRandom(currentSeed) * 2;
            currentSeed++;
            const height = 2 + seededRandom(currentSeed) * 4;
            patternCtx.fillRect(x, y, width, height);
        }
        
        // Add some lighter grass
        patternCtx.fillStyle = '#388E3C';
        for (let i = 0; i < 10; i++) {
            currentSeed++;
            const x = seededRandom(currentSeed) * 40;
            currentSeed++;
            const y = seededRandom(currentSeed) * 40;
            const width = 1;
            currentSeed++;
            const height = 1 + seededRandom(currentSeed) * 2;
            patternCtx.fillRect(x, y, width, height);
        }
        
        this.grassPattern = this.ctx.createPattern(patternCanvas, 'repeat');
    }
    
    clear() {
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    }
    
    drawBackground() {
        // Draw grass background
        this.ctx.fillStyle = this.grassPattern;
        this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
        
        // Add some subtle gradient overlay
        const gradient = this.ctx.createRadialGradient(
            this.canvas.width / 2, this.canvas.height / 2, 0,
            this.canvas.width / 2, this.canvas.height / 2, Math.max(this.canvas.width, this.canvas.height) / 2
        );
        gradient.addColorStop(0, 'rgba(46, 125, 50, 0.1)');
        gradient.addColorStop(1, 'rgba(27, 94, 32, 0.3)');
        
        this.ctx.fillStyle = gradient;
        this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    }
    
    drawPitchMarkings() {
        this.ctx.strokeStyle = COLORS.LINE_WHITE;
        this.ctx.lineWidth = 2;
        this.ctx.setLineDash([]);
        
        // Center line
        this.ctx.beginPath();
        this.ctx.moveTo(PITCH_MARKINGS.CENTER_LINE.x, PITCH_MARKINGS.CENTER_LINE.y);
        this.ctx.lineTo(PITCH_MARKINGS.CENTER_LINE.x, PITCH_MARKINGS.CENTER_LINE.y + PITCH_MARKINGS.CENTER_LINE.height);
        this.ctx.stroke();
        
        // Center circle
        this.ctx.beginPath();
        this.ctx.arc(
            PITCH_MARKINGS.CENTER_CIRCLE.x, 
            PITCH_MARKINGS.CENTER_CIRCLE.y, 
            PITCH_MARKINGS.CENTER_CIRCLE.radius, 
            0, 
            Math.PI * 2
        );
        this.ctx.stroke();
        
        // Goal areas
        PITCH_MARKINGS.GOAL_AREAS.forEach(goal => {
            this.ctx.strokeRect(goal.x, goal.y, goal.width, goal.height);
        });
        
        // Corner arcs (small quarter circles)
        const cornerRadius = 15;
        const corners = [
            { x: 0, y: 0 },
            { x: this.canvas.width, y: 0 },
            { x: 0, y: this.canvas.height },
            { x: this.canvas.width, y: this.canvas.height }
        ];
        
        corners.forEach((corner, index) => {
            this.ctx.beginPath();
            const startAngle = index * Math.PI / 2;
            const endAngle = startAngle + Math.PI / 2;
            this.ctx.arc(corner.x, corner.y, cornerRadius, startAngle, endAngle);
            this.ctx.stroke();
        });
        
        // Add some subtle field wear patterns
        this.drawFieldWear();
    }
    
    drawFieldWear() {
        // Add some subtle wear patterns to make the field look more realistic
        this.ctx.fillStyle = 'rgba(255, 255, 255, 0.05)';
        
        // Goal areas wear
        this.ctx.fillRect(10, this.canvas.height / 2 - 30, 60, 60);
        this.ctx.fillRect(this.canvas.width - 70, this.canvas.height / 2 - 30, 60, 60);
        
        // Center circle wear
        this.ctx.beginPath();
        this.ctx.arc(
            PITCH_MARKINGS.CENTER_CIRCLE.x, 
            PITCH_MARKINGS.CENTER_CIRCLE.y, 
            PITCH_MARKINGS.CENTER_CIRCLE.radius - 10, 
            0, 
            Math.PI * 2
        );
        this.ctx.fill();
        
        // Some random wear spots
        for (let i = 0; i < 8; i++) {
            const x = Math.random() * this.canvas.width;
            const y = Math.random() * this.canvas.height;
            const radius = 5 + Math.random() * 15;
            
            this.ctx.beginPath();
            this.ctx.arc(x, y, radius, 0, Math.PI * 2);
            this.ctx.fill();
        }
    }
    
    drawPowerUpEffects(activeEffects) {
        if (activeEffects.length === 0) return;
        
        // Draw screen-wide effects for active power-ups
        activeEffects.forEach(effect => {
            if (effect.id === 'color_change') {
                this.drawRainbowEffect();
            } else if (effect.multiplier) {
                this.drawMultiplierEffect(effect);
            }
        });
    }
    
    drawRainbowEffect() {
        // Mobile-optimized rainbow border effect
        const isMobile = this.isMobileDevice();
        
        if (isMobile) {
            // Simplified mobile version - single animated border
            this.drawMobileRainbowEffect();
        } else {
            // Full desktop version
            this.drawDesktopRainbowEffect();
        }
    }
    
    isMobileDevice() {
        // Detect mobile devices
        return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
               (window.innerWidth <= 768) ||
               ('ontouchstart' in window);
    }
    
    drawMobileRainbowEffect() {
        // Simplified rainbow effect for mobile
        const time = Date.now() * 0.003; // Slower animation
        const colors = ['#FF0000', '#FF7F00', '#FFFF00', '#00FF00', '#0000FF', '#4B0082', '#9400D3'];
        const colorIndex = Math.floor(time) % colors.length;
        const nextColorIndex = (colorIndex + 1) % colors.length;
        
        this.ctx.save();
        this.ctx.globalAlpha = 0.4;
        this.ctx.lineWidth = 6;
        this.ctx.strokeStyle = colors[colorIndex];
        
        // Simple solid border instead of dashed lines
        this.ctx.strokeRect(3, 3, this.canvas.width - 6, this.canvas.height - 6);
        
        // Add subtle glow effect
        this.ctx.globalAlpha = 0.2;
        this.ctx.lineWidth = 12;
        this.ctx.strokeRect(0, 0, this.canvas.width, this.canvas.height);
        
        this.ctx.restore();
    }
    
    drawDesktopRainbowEffect() {
        // Full desktop rainbow effect
        const time = Date.now() * 0.005;
        const colors = ['#FF0000', '#FF7F00', '#FFFF00', '#00FF00', '#0000FF', '#4B0082', '#9400D3'];
        
        this.ctx.save();
        this.ctx.globalAlpha = 0.3;
        this.ctx.lineWidth = 4;
        
        for (let i = 0; i < colors.length; i++) {
            this.ctx.strokeStyle = colors[i];
            this.ctx.setLineDash([10, 5]);
            this.ctx.lineDashOffset = time * 20 + i * 5;
            
            this.ctx.strokeRect(
                i * 2, i * 2, 
                this.canvas.width - i * 4, 
                this.canvas.height - i * 4
            );
        }
        
        this.ctx.restore();
    }
    
    drawMultiplierEffect(effect) {
        // Mobile-optimized glowing effect
        const isMobile = this.isMobileDevice();
        
        this.ctx.save();
        this.ctx.globalAlpha = isMobile ? 0.3 : 0.2;
        
        if (isMobile) {
            // Simplified mobile version - no shadow blur for better performance
            const pulse = Math.sin(Date.now() * 0.005) * 0.1 + 0.9; // Slower pulse
            const glowSize = 8 * pulse;
            
            this.ctx.strokeStyle = effect.color;
            this.ctx.lineWidth = glowSize;
            this.ctx.strokeRect(glowSize / 2, glowSize / 2, this.canvas.width - glowSize, this.canvas.height - glowSize);
        } else {
            // Full desktop version with shadow effects
            this.ctx.shadowColor = effect.color;
            this.ctx.shadowBlur = 20;
            
            const pulse = Math.sin(Date.now() * 0.01) * 0.1 + 0.9;
            const glowSize = 10 * pulse;
            
            this.ctx.strokeStyle = effect.color;
            this.ctx.lineWidth = glowSize;
            this.ctx.strokeRect(glowSize / 2, glowSize / 2, this.canvas.width - glowSize, this.canvas.height - glowSize);
        }
        
        this.ctx.restore();
    }
    
    drawText(text, x, y, options = {}) {
        const {
            fontSize = 16,
            color = '#FFFFFF',
            align = 'center',
            baseline = 'middle',
            font = 'Arial'
        } = options;
        
        this.ctx.save();
        this.ctx.font = `${fontSize}px ${font}`;
        this.ctx.fillStyle = color;
        this.ctx.textAlign = align;
        this.ctx.textBaseline = baseline;
        
        // Add text shadow for better readability
        this.ctx.shadowColor = 'rgba(0, 0, 0, 0.8)';
        this.ctx.shadowBlur = 2;
        this.ctx.shadowOffsetX = 1;
        this.ctx.shadowOffsetY = 1;
        
        this.ctx.fillText(text, x, y);
        this.ctx.restore();
    }
    
    drawButton(text, x, y, width, height, options = {}) {
        const {
            backgroundColor = '#4CAF50',
            textColor = '#FFFFFF',
            borderColor = '#2E7D32',
            fontSize = 14,
            borderRadius = 8
        } = options;
        
        this.ctx.save();
        
        // Draw button background
        this.ctx.fillStyle = backgroundColor;
        this.ctx.fillRect(x, y, width, height);
        
        // Draw button border
        this.ctx.strokeStyle = borderColor;
        this.ctx.lineWidth = 2;
        this.ctx.strokeRect(x, y, width, height);
        
        // Draw button text
        this.drawText(text, x + width / 2, y + height / 2, {
            fontSize,
            color: textColor,
            align: 'center',
            baseline: 'middle'
        });
        
        this.ctx.restore();
    }
}
